/* eslint-disable @typescript-eslint/no-empty-function */
import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonSpinner,
  IonTitle,
} from '@ionic/react'
import { chevronBack } from 'ionicons/icons'

import { Avatar } from '@/components/atoms/Avatar'
import { Button } from '@/components/atoms/Button'
import { TextArea } from '@/components/atoms/TextArea'
import { Title } from '@/components/atoms/Title'
import { UserName } from '@/components/atoms/UserName'
import { Main } from '@/components/templates/main'

import { UserInfo } from '@/services/user'

import { IProgress } from '@/interfaces'

interface IContestCreateUploadVideo {
  onModalComplete: (videoDescription: string) => void
  onDismiss: () => void
  videoDataUrl: string
  user: UserInfo
  isLoading: boolean
  progress: IProgress
}

export const ContestCreateUploadVideo: FC<IContestCreateUploadVideo> = ({
  videoDataUrl,
  user,
  isLoading,
  progress,
  onModalComplete,
  onDismiss,
}) => {
  const { t } = useTranslation('common')
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      description: '',
    },
    mode: 'all',
  })

  const onSubmit = ({ description }) => {
    onModalComplete(description)
  }

  const header = (
    <>
      <IonButtons slot="secondary">
        <IonButton
          mode="md"
          className="round plain ion-color"
          onClick={() => onDismiss()}
        >
          <IonIcon
            slot="icon-only"
            className="text-primary"
            icon={chevronBack}
          />
        </IonButton>
      </IonButtons>
      <IonButtons slot="primary">
        <Button
          onClick={handleSubmit(onSubmit)}
          className="submit plain"
          isDisabled={!isValid || isLoading}
        >
          {t('contest.create.saveVideo')}
        </Button>
      </IonButtons>
      <IonTitle>
        <Title className="edit-title" level={2}>
          {t('contest.create.createContestTitle')}
        </Title>
      </IonTitle>
    </>
  )

  return (
    <Main header={header}>
      <div className="flex min-h-full justify-center items-center p-4">
        <div className="text-center mb-8 w-full">
          {isLoading ? (
            <>
              <IonSpinner className="w-20 h-20 mb-4" color="main" />
              <b className="block w-full text-white">
                {t('contest.create.uploadingVideo')} <br />
                {`${
                  progress
                    ? ((progress?.loaded / progress?.total) * 100).toFixed(0)
                    : 0
                }%`}
              </b>
            </>
          ) : (
            <>
              <IonItem>
                <span className="pr-2">
                  <Avatar
                    userId={user?.id}
                    isModerator={user?.isModerator}
                    isAmbassador={user?.isAmbassador}
                    badge={user?.badge}
                  />
                </span>
                <UserName name={user.username} />
              </IonItem>
              <IonItem>
                <div className="w-full ml-[3rem]">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                      control={control}
                      name="description"
                      rules={{ maxLength: 3e4 }}
                      render={({ field }) => (
                        <TextArea
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value)
                          }}
                          className="post w-full bg-dark"
                          placeholder={t('contest.captionPlaceholder')}
                        />
                      )}
                    />
                  </form>
                </div>
              </IonItem>
              <video
                className="mb-8 max-h-[37.5rem] mx-auto"
                src={videoDataUrl}
                controls={true}
              />
            </>
          )}
        </div>
      </div>
    </Main>
  )
}
